/* ════════════════════════════════════════════════════════════════════
 *  MDL Studies — typed API client (read path).
 *  GET /api/v1/mdl-studies         → list grid
 *  GET /api/v1/mdl-studies/{id}    → full study detail
 *  Backed by the legacy dbo.MDLStudy* tables (database-first wrap).
 * ════════════════════════════════════════════════════════════════════ */
import { api } from '../../../api/client';

export interface ApiMdlListItem {
  key: string; mdlId: string | null; matrix: string | null; test: string | null;
  method: string | null; instrument: string | null; modelType: number; ver: string | null;
  finalMdl: string | null; spikes: number; blanks: number; days: number; pair: boolean;
  studyDate: string | null; effective: string | null; nextDue: string | null;
  qcStatus: string | null; active: boolean; mdlStatus: number; createdBy: string | null; esigCount: number;
}

export interface ApiMdlResult {
  oid: string; parameter: string | null; units: string | null; n: number | null;
  sdSpikes: number | null; tValue: number | null; mdlS: number | null; mdlB: number | null;
  finalMdl: number | null; spikeConc: number | null; blanksGreaterMdl: number | null;
  rule: string | null; qcBatchId: string | null; percentRec: string | null;
  currentMdl: number | null; ratio: number | null; verification: string | null;
}
export interface ApiMdlReplicate {
  resultOid: string; kind: string | null; seq: number;
  batchId: string | null; sampleId: string | null; sysSampleCode: string | null; resultText: string | null; analyzedDate: string | null;
  spikeConc: number | null; measuredValue: number | null; numerical: boolean | null; note: string | null;
}
export interface ApiMdlHeader {
  key: string; mdlId: string | null; matrix: string | null; test: string | null; method: string | null;
  instrument: string | null; modelType: number; methodVersion: string | null; spikeLevel: string | null;
  spikes: number; blanks: number; days: number; qcStatus: string | null; active: boolean; mdlStatus: number;
  qcType: string | null; qcCategory: string | null; createdBy: string | null;
  studyDate: string | null; effective: string | null; nextDue: string | null;
}
export interface ApiMdlReview {
  action: string | null; reviewedBy: string | null; reviewedDate: string | null; comment: string | null; hash: string | null;
  /** eSig block fields (2026-10-26) — stored with the signature, returned so the report needs nothing typed. */
  meaning?: string | null; role?: string | null; userId?: string | null; mode?: string | null; mfa?: boolean | null;
}
export interface ApiMdlAttachment { oid: string; name: string | null; date: string | null; operator: string | null; sizeBytes: number | null; contentType: string | null; }
export interface ApiMdlDetail {
  header: ApiMdlHeader; results: ApiMdlResult[]; replicates: ApiMdlReplicate[];
  reviews: ApiMdlReview[]; attachments: ApiMdlAttachment[];
}

export async function fetchMdlList(signal?: AbortSignal): Promise<ApiMdlListItem[]> {
  const { data } = await api.get<{ success: boolean; data: ApiMdlListItem[] }>('/api/v1/mdl-studies', { signal });
  return data.data ?? [];
}

export async function fetchMdlDetail(id: string, signal?: AbortSignal): Promise<ApiMdlDetail> {
  const { data } = await api.get<{ success: boolean; data: ApiMdlDetail }>(`/api/v1/mdl-studies/${id}`, { signal });
  return data.data;
}

/* ── Create (Save) — Phase 2 write path ── */
export interface MdlSpikeInput { batch: string | null; sample: string | null; date: string | null; spikeConc: number; result: number; sysSampleCode?: string | null; resultText?: string | null; }
export interface MdlBlankInput { batch: string | null; sample: string | null; date: string | null; result: number; numerical: boolean; note: string | null; sysSampleCode?: string | null; resultText?: string | null; }
export interface MdlSignatureInput { action: string; meaning: string; reason: string | null; mode: string; mfa: boolean; name: string | null; role: string | null; userId: string | null; }
export interface MdlCreateInput {
  matrix: string | null; test: string | null; method: string | null; instrument: string | null;
  matrixId: string | null; testId: string | null; methodId: string | null;
  modelType: number; methodVersion: string | null; spikeLevel: string | null; analyte: string | null; units: string | null;
  referenceMdl: number | null;
  spikes: MdlSpikeInput[]; blanks: MdlBlankInput[]; signature: MdlSignatureInput;
}

/* ── Study-parameter lookups (Matrix → Test grid → Method/Instrument/Parameter) ── */
export interface MdlInstrumentOption { number: string | null; name: string | null; }
export interface MdlQueryLookup {
  matrixId: string | null; matrixName: string | null; testMethodId: string | null;
  testName: string | null; methodId: string | null; methodNumber: string | null;
  parameters: string[]; instruments: MdlInstrumentOption[];
}
export async function fetchMdlLookups(signal?: AbortSignal): Promise<MdlQueryLookup[]> {
  const { data } = await api.get<{ success: boolean; data: MdlQueryLookup[] }>('/api/v1/mdl-studies/lookups', { signal });
  return data.data ?? [];
}
export async function fetchMdlQcCategories(signal?: AbortSignal): Promise<string[]> {
  const { data } = await api.get<{ success: boolean; data: string[] }>('/api/v1/mdl-studies/qc-categories', { signal });
  return data.data ?? [];
}
/* Workflow level config (dbo.ResultEntryDefaultSettings) — whether Validate / Approve
   levels are shown or skipped (ResultValidation / ResultApproval = 0 → show the level). */
export interface MdlWorkflowConfig {
  resultValidation: number; resultApproval: number; skipValidate: boolean; skipApprove: boolean;
}
export async function fetchMdlWorkflowConfig(signal?: AbortSignal): Promise<MdlWorkflowConfig> {
  const { data } = await api.get<{ success: boolean; data: MdlWorkflowConfig }>('/api/v1/mdl-studies/workflow-config', { signal });
  return data.data ?? { resultValidation: 0, resultApproval: 0, skipValidate: false, skipApprove: false };
}
/* QC Types for the selected Test (MDL Verification query lookup) — QCType / QCRole / QCRootRole. */
export interface MdlQcTypeOption {
  qcType: string | null; qcRole: string | null; qcRootRole: string | null; isSpike: boolean; isBlank: boolean;
}
export async function fetchMdlQcTypes(testMethodId: string, signal?: AbortSignal): Promise<MdlQcTypeOption[]> {
  const { data } = await api.get<{ success: boolean; data: MdlQcTypeOption[] }>(
    '/api/v1/mdl-studies/qc-types', { params: { testMethodId }, signal });
  return data.data ?? [];
}
export async function fetchMdlReplicateCount(testMethodId: string, from: string, to: string, signal?: AbortSignal): Promise<number> {
  const { data } = await api.get<{ success: boolean; data: { count: number } }>('/api/v1/mdl-studies/replicate-count', { params: { testMethodId, from, to }, signal });
  return data.data?.count ?? 0;
}

/* ── QC batches for the Query Panel's Spike Run Pick / Blank Batch Pick ──
   The runs behind the study, ticked rather than typed. `samples` is what
   40 CFR 136 App. B §2(b) counts — the minimum of 7 is in REPLICATES, not runs —
   so the panel sums it over the ticked batches. */
export interface MdlQcBatchOption {
  batchId: string | null;
  kind: string | null;      // 'S' = spiked · 'B' = method blank
  qcType: string | null;
  date: string | null;      // yyyy-MM-dd
  samples: number;
  analytes: number;
  source?: string | null;   // 'ALPACA' or 'BTLIMS' (2026-10-26)
}
export async function fetchMdlQcBatches(
  testMethodId: string, from: string, to: string,
  opts?: { qcCategories?: string[]; qcTypes?: string[]; instruments?: string[] | string | null }, signal?: AbortSignal,
): Promise<MdlQcBatchOption[]> {
  const params: Record<string, string> = { testMethodId, from, to };
  if (opts?.qcTypes && opts.qcTypes.length) params.qcTypes = opts.qcTypes.join(',');
  else if (opts?.qcCategories && opts.qcCategories.length) params.qcCategories = opts.qcCategories.join(',');
  const inst = Array.isArray(opts?.instruments) ? opts!.instruments.filter(Boolean).join(',') : opts?.instruments;
  if (inst) params.instrument = inst;
  const { data } = await api.get<{ success: boolean; data: MdlQcBatchOption[] }>(
    '/api/v1/mdl-studies/qc-batches', { params, signal });
  return data.data ?? [];
}

/* ── Real SDMS retrieve (qualifying SampleParameter rows; LFB=spiked / LRB=blank) ── */
export interface MdlRetrieveRow {
  analyte: string | null; qcBatchId: string | null; sampleId: string | null;
  sysSampleCode: string | null;    // QC POSITION in the run ('LCS Digested1') — a QC type
  loginSampleCode: string | null;  // the QC sample as LOGGED IN (Samplelogin, flagged LFB*/LRB*)
  instrument: string | null;
  result: string | null; qcCategory: string | null;
  qcTypeName: string | null;    // Verification: TestParameter.QCType.QCTypeName
  kind: string | null;          // 'S' = spiked/standard · 'B' = blank
  value: number | null; spikeAmount: number | null; currentMdl: number | null; units: string | null; analyzedDate: string | null;
  /** 'ALPACA' or 'BTLIMS' (dbo.BTLIMSQCDC history used for the ongoing MDL, 2026-10-26). */
  source?: string | null;
}
/** Initial MDL retrieve (qcCategories) OR — when qcTypes is supplied — MDL Verification retrieve. */
export async function fetchMdlRetrieve(
  testMethodId: string, from: string, to: string,
  opts?: { qcCategories?: string[]; qcTypes?: string[]; instruments?: string[] | string | null }, signal?: AbortSignal,
): Promise<MdlRetrieveRow[]> {
  const params: Record<string, string> = { testMethodId, from, to };
  if (opts?.qcTypes && opts.qcTypes.length) params.qcTypes = opts.qcTypes.join(',');
  else if (opts?.qcCategories && opts.qcCategories.length) params.qcCategories = opts.qcCategories.join(',');
  // Scope to the selected instrument(s) (gap 3.5 — multi-select). Comma-joined; the
  // backend splits with STRING_SPLIT and matches any (a single value works too).
  const inst = Array.isArray(opts?.instruments) ? opts!.instruments.filter(Boolean).join(',') : opts?.instruments;
  if (inst) params.instrument = inst;
  const { data } = await api.get<{ success: boolean; data: MdlRetrieveRow[] }>('/api/v1/mdl-studies/retrieve', { params, signal });
  return data.data ?? [];
}

/* Reference-MDL options (prior Initial-MDL studies) for the Verification query panel. */
export interface MdlReferenceOption {
  key: string | null; mdlId: string | null; testName: string | null; methodName: string | null;
  finalMdl: string | null; mdlStatus: number; studyDate: string | null;
}
export async function fetchMdlReferenceMdls(methodId?: string | null, testName?: string | null, signal?: AbortSignal): Promise<MdlReferenceOption[]> {
  const params: Record<string, string> = {};
  if (methodId) params.methodId = methodId;
  if (testName) params.testName = testName;
  const { data } = await api.get<{ success: boolean; data: MdlReferenceOption[] }>('/api/v1/mdl-studies/reference-mdls', { params, signal });
  return data.data ?? [];
}
/* Per-analyte trailing-24-month method-blank exceedance vs the established MDL (Verification §4 / gap 2.2). */
export interface MdlBlankExceedance { parameter: string | null; totalBlanks: number; exceedCount: number; }
export async function fetchMdlBlankExceedance(testMethodId: string, instrument?: string[] | string | null, signal?: AbortSignal): Promise<MdlBlankExceedance[]> {
  const params: Record<string, string> = { testMethodId };
  // Multi-instrument (gap 3.5): the selected instruments are passed as an array and
  // comma-joined; the backend splits with STRING_SPLIT and matches ANY of them (a
  // single value works too). Accepts a bare string as well.
  const inst = Array.isArray(instrument) ? instrument.filter(Boolean).join(',') : instrument;
  if (inst) params.instrument = inst;
  const { data } = await api.get<{ success: boolean; data: MdlBlankExceedance[] }>('/api/v1/mdl-studies/blank-exceedance', { params, signal });
  return data.data ?? [];
}

export async function createMdlStudy(input: MdlCreateInput): Promise<{ id: string; mdlId: string }> {
  const { data } = await api.post<{ success: boolean; data: { id: string; mdlId: string } }>('/api/v1/mdl-studies', input);
  return data.data;
}

/* ── Multi-analyte create (Save) — one result per analyte from the retrieved matrix ── */
export interface MdlAnalyteInput {
  parameter: string | null; units: string | null; spikeLevel: number; referenceMdl: number | null;
  spikes: MdlSpikeInput[]; blanks: MdlBlankInput[];
}
export interface MdlExclusionInput { sysSampleCode: string | null; qcBatchId: string | null; qcType: string | null; reason: string; }
export interface MdlCreateMultiInput {
  matrix: string | null; test: string | null; method: string | null; instrument: string | null;
  matrixId: string | null; testId: string | null; methodId: string | null;
  modelType: number; methodVersion: string | null; dateFrom: string | null; dateTo: string | null;
  analytes: MdlAnalyteInput[]; signature: MdlSignatureInput;
  exclusions?: MdlExclusionInput[];   // outlier replicates excluded with justification (gap 3.4)
  qcCategory?: string | null;         // Initial MDL — CSV of retrieved QC Categories
  qcType?: string | null;             // Verification — CSV of selected QC Types
}
export async function createMdlStudyMulti(input: MdlCreateMultiInput): Promise<{ id: string; mdlId: string }> {
  const { data } = await api.post<{ success: boolean; data: { id: string; mdlId: string } }>('/api/v1/mdl-studies/multi', input);
  return data.data;
}

/* ── Re-save a rolled-back study (reopen at Calculate & Save) — replaces its data, keeps the MdlId ── */
export async function updateMdlStudyMulti(id: string, input: MdlCreateMultiInput): Promise<{ id: string; mdlId: string }> {
  const { data } = await api.put<{ success: boolean; data: { id: string; mdlId: string } }>(`/api/v1/mdl-studies/${id}/multi`, input);
  return data.data;
}

/* ── Attachments (IFileStore-backed) ── */
export async function uploadMdlAttachment(id: string, file: File): Promise<{ id: string; name: string }> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<{ success: boolean; data: { id: string; name: string } }>(`/api/v1/mdl-studies/${id}/attachments`, form);
  return data.data;
}
export async function deleteMdlAttachment(id: string, attId: string): Promise<void> {
  await api.delete(`/api/v1/mdl-studies/${id}/attachments/${attId}`);
}
export async function downloadMdlAttachment(id: string, attId: string, fileName: string): Promise<void> {
  const res = await api.get(`/api/v1/mdl-studies/${id}/attachments/${attId}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName || 'attachment';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ── Soft-delete a study (blocked server-side when Released → HTTP 409) ── */
export async function deleteMdlStudy(id: string): Promise<void> {
  await api.delete(`/api/v1/mdl-studies/${id}`);
}

/* ── Apply the released study's MDLs to TestParameter.MDL (QCType=Sample) ──
 *  parameters (optional, C12): only write these analyte names back; omit = all. */
export async function applyMdl(id: string, parameters?: string[]): Promise<{ mdlId: string; analytes: number; rows: number }> {
  const body = parameters && parameters.length ? { parameters } : {};
  const { data } = await api.post<{ success: boolean; data: { mdlId: string; analytes: number; rows: number } }>(`/api/v1/mdl-studies/${id}/apply-mdl`, body);
  return data.data;
}

/* ── Workflow transition (Submit / Validate / Approve / Release / Rollback) — Phase 2 write ── */
export type MdlAction = 'Submit' | 'Validate' | 'Approve' | 'Release' | 'Rollback';
export async function transitionMdlStudy(id: string, action: MdlAction, signature: MdlSignatureInput): Promise<{ status: number }> {
  const { data } = await api.post<{ success: boolean; data: { status: number } }>(`/api/v1/mdl-studies/${id}/review`, { action, signature });
  return data.data;
}
